import { get } from "svelte/store"
import { apiBaseOverride } from "../../../_shared/_logic/store"
import { baseAPIURL } from "../../_shared/_logic/config"
import { ApiError } from "../presentation"
import { parseApiErrorPayload, resolveApiErrorMessage } from "../../error-message"

export type AppointmentPortalState =
  | "invalid"
  | "locked"
  | "proposal_pending"
  | "confirmed"
  | "no_appointment"
  | "wrong_recipient_reported"

export type AppointmentPortalAction = "confirm" | "search_slots" | "cancel" | "wrong_recipient"

export type AppointmentPortalInvalidReason =
  | "closed_work_order"
  | "incomplete"
  | "missing_code_context"
  | "missing_contact_order"
  | "missing_property"
  | "missing_property_unit"
  | "missing_unique_customer"
  | "missing_work_order"
  | "not_found"
  | (string & {})

export type PublicAppointmentPortalOrganization = {
  id: string
  name: string
  slug: string
  contact_email: string | null
}

export type PublicAppointmentPortalProperty = {
  id: string
  label: string | null
  unit_id: string | null
  unit_label: string | null
}

export type PublicAppointmentPortalWorkOrder = {
  id: string
  order_number: string | null
  order_type_public_label: string | null
  order_type_public_labels?: string[]
  order_type_description: string | null
  status: string
  estimated_duration_min: number | null
  due_date: string | null
}

export type PublicAppointmentDeadlineWarning = {
  code: "AFTER_DUE_DATE"
  due_date: string
  slot_date: string
}

export type PublicAppointmentSummary = {
  id: string
  status: string
  start_at: string
  end_at: string
  employee_id: string | null
  employee_name: string | null
  contact_name?: string | null
  flex_type: string | null
  timeframe_start_at: string | null
  timeframe_end_at: string | null
  customer_window_start_at?: string | null
  customer_window_end_at?: string | null
}

export type PublicAppointmentSlot = {
  start_at: string
  end_at: string
  employee_id: string | null
  employee_name: string | null
  flex_type: string
  timeframe_start_at: string | null
  timeframe_end_at: string | null
  customer_window_start_at?: string | null
  customer_window_end_at?: string | null
  deadline_warning?: PublicAppointmentDeadlineWarning | null
}

export type PublicAppointmentSlotSearchPolicy = {
  min_lead_days: number
  is_reschedule: boolean
  earliest_start_at: string
  earliest_date: string
}

export type PublicAppointmentPortalStateResponse = {
  code: string
  state: AppointmentPortalState
  invalid_reason: AppointmentPortalInvalidReason | null
  organization: PublicAppointmentPortalOrganization | null
  property: PublicAppointmentPortalProperty | null
  work_order: PublicAppointmentPortalWorkOrder | null
  appointment: PublicAppointmentSummary | null
  slot_search_policy: PublicAppointmentSlotSearchPolicy | null
  actions: AppointmentPortalAction[]
}

export type PublicAppointmentSlotSearchRequest = {
  count?: number
  date?: string | null
  include_flex?: boolean
  target_start_at?: string | null
  route_policy_token?: string | null
}

export type PublicAppointmentSlotSearchResponse = {
  generated_at: string
  slots: PublicAppointmentSlot[]
}

export type PublicAppointmentSlotRecommendationsRequest = Record<string, never>

export type PublicAppointmentSlotRecommendation = Pick<
  PublicAppointmentSlot,
  | "start_at"
  | "end_at"
  | "employee_id"
  | "employee_name"
  | "flex_type"
  | "timeframe_start_at"
  | "timeframe_end_at"
  | "customer_window_start_at"
  | "customer_window_end_at"
  | "deadline_warning"
>

export type PublicAppointmentSlotRecommendationsResponse = {
  generated_at: string
  slots: PublicAppointmentSlotRecommendation[]
}

export type PublicAppointmentSlotCalendarDay = {
  date: string
  available: boolean
  hard_count: number
  flex_count: number
  has_deadline_warning?: boolean
}

export type PublicAppointmentSlotCalendarRequest = {
  start_date?: string | null
  weeks?: number
}

export type PublicAppointmentSlotCalendarWeek = {
  start_date: string
  end_date: string
  days: PublicAppointmentSlotCalendarDay[]
}

export type PublicAppointmentSlotCalendarResponse = {
  generated_at: string
  weeks: PublicAppointmentSlotCalendarWeek[]
  route_policy_token?: string | null
}

export type PublicAppointmentParticipantInput = {
  display_name: string
  email?: string | null
  phone?: string | null
  note?: string | null
  customer_role_hint?: "TENANT" | "MANAGER" | "OWNER" | "OTHER" | null
  idempotency_key: string
}

export type PublicAppointmentPortalMutationResponse = {
  contact_completion?: PublicAppointmentContactCompletion | null
  state: PublicAppointmentPortalStateResponse
}

export type PublicAppointmentContactCompletion = {
  email: "present" | "missing" | "unknown"
  phone: "present" | "missing" | "unknown"
}

export type PublicAppointmentContactSupplement = {
  email: string | null
  phone: string | null
  contact_use_accepted: true
  idempotency_key: string
}

export type PublicAppointmentWrongRecipientRequest = {
  message?: string | null
  suggested_contact_name?: string | null
  suggested_contact_value?: string | null
}

export type PublicContactMethodChannel =
  | "EMAIL"
  | "SMS"
  | "WHATSAPP"
  | "MOBILE_MESSAGE"
  | "PHONE"
  | "LETTER"
  | (string & {})

export type PublicContactMethodKind = "EMAIL" | "MOBILE" | "FIXED_LINE" | "ADDRESS" | (string & {})

export type PublicContactMethodChannelResponse = {
  channel: PublicContactMethodChannel
  label: string
  is_enabled: boolean
  disabled_reason?: string | null
}

export type PublicContactMethodResponse = {
  id: string
  kind: PublicContactMethodKind
  label: string
  masked_value: string
  channels: PublicContactMethodChannelResponse[]
  deletion_requested: boolean
  is_pending?: boolean
  read_only?: boolean
}

export type PublicContactMethodsResponse = {
  available: boolean
  unavailable_reason?: string | null
  contact_methods: PublicContactMethodResponse[]
}

export type ContactMethodDeletionRequestResponse = {
  deletion_requested: boolean
  case_id?: string | null
  contact_method?: PublicContactMethodResponse | null
}

export type SetContactMethodChannelPermissionRequest = {
  channel: PublicContactMethodChannel
  is_enabled: boolean
}

const BASE_PATH = "/public/appointment-portal"

const resolveBaseUrl = () => {
  const override = get(apiBaseOverride)

  return override ?? baseAPIURL
}

const joinUrl = (path: string) => {
  const base = resolveBaseUrl().replace(/\/+$/, "")
  const normalized = path.startsWith("/") ? path : `/${path}`

  return `${base}${normalized}`
}

const portalUrl = (orgIdentifier: string, code: string, suffix = "") => {
  const targetOrg = orgIdentifier.trim()
  const targetCode = code.trim().toUpperCase()

  if (!targetOrg || !targetCode) {
    throw new ApiError(400, "Ungültiger Termincode.")
  }

  return joinUrl(
    `${BASE_PATH}/${encodeURIComponent(targetOrg)}/${encodeURIComponent(targetCode)}${suffix}`,
  )
}

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const { detail } = await parseApiErrorPayload(response)
    const message = resolveApiErrorMessage(detail, {
      statusCode: response.status,
      statusText: response.statusText,
    })
    throw new ApiError(response.status, message, detail)
  }

  return (await response.json()) as T
}

export const fetchAppointmentPortalState = async (
  orgIdentifier: string,
  code: string,
): Promise<PublicAppointmentPortalStateResponse> => {
  const response = await fetch(portalUrl(orgIdentifier, code))

  return parseResponse<PublicAppointmentPortalStateResponse>(response)
}

export const searchAppointmentSlots = async (
  orgIdentifier: string,
  code: string,
  payload: PublicAppointmentSlotSearchRequest = {},
  options: { signal?: AbortSignal } = {},
): Promise<PublicAppointmentSlotSearchResponse> => {
  const response = await fetch(portalUrl(orgIdentifier, code, "/slots/search"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
    signal: options.signal,
  })

  return parseResponse<PublicAppointmentSlotSearchResponse>(response)
}

export const fetchAppointmentSlotRecommendations = async (
  orgIdentifier: string,
  code: string,
  options: { signal?: AbortSignal } = {},
): Promise<PublicAppointmentSlotRecommendationsResponse> => {
  const payload: PublicAppointmentSlotRecommendationsRequest = {}
  const response = await fetch(portalUrl(orgIdentifier, code, "/slots/recommendations"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
    signal: options.signal,
  })

  return parseResponse<PublicAppointmentSlotRecommendationsResponse>(response)
}

export const fetchAppointmentSlotCalendar = async (
  orgIdentifier: string,
  code: string,
  payload: PublicAppointmentSlotCalendarRequest = {},
  options: { signal?: AbortSignal } = {},
): Promise<PublicAppointmentSlotCalendarResponse> => {
  const response = await fetch(portalUrl(orgIdentifier, code, "/slots/calendar"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
    signal: options.signal,
  })

  return parseResponse<PublicAppointmentSlotCalendarResponse>(response)
}

export const confirmAppointment = async (
  orgIdentifier: string,
  code: string,
  participant: PublicAppointmentParticipantInput,
): Promise<PublicAppointmentPortalMutationResponse> => {
  const response = await fetch(portalUrl(orgIdentifier, code, "/confirm"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ participant }),
  })

  return parseResponse<PublicAppointmentPortalMutationResponse>(response)
}

export const bookAppointmentSlot = async (
  orgIdentifier: string,
  code: string,
  slot: PublicAppointmentSlot,
  participant: PublicAppointmentParticipantInput,
): Promise<PublicAppointmentPortalMutationResponse> => {
  const response = await fetch(portalUrl(orgIdentifier, code, "/book"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ slot, participant }),
  })

  return parseResponse<PublicAppointmentPortalMutationResponse>(response)
}

export const cancelAppointment = async (
  orgIdentifier: string,
  code: string,
  participant: PublicAppointmentParticipantInput,
): Promise<PublicAppointmentPortalMutationResponse> => {
  const response = await fetch(portalUrl(orgIdentifier, code, "/cancel"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ participant }),
  })

  return parseResponse<PublicAppointmentPortalMutationResponse>(response)
}

export const reportWrongRecipient = async (
  orgIdentifier: string,
  code: string,
  payload: PublicAppointmentWrongRecipientRequest,
): Promise<PublicAppointmentPortalMutationResponse> => {
  const response = await fetch(portalUrl(orgIdentifier, code, "/wrong-recipient"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  })

  return parseResponse<PublicAppointmentPortalMutationResponse>(response)
}

export const listContactMethods = async (
  orgIdentifier: string,
  code: string,
): Promise<PublicContactMethodsResponse> => {
  const response = await fetch(portalUrl(orgIdentifier, code, "/contact-methods"))

  return parseResponse<PublicContactMethodsResponse>(response)
}

export const supplementAppointmentContactDetails = async (
  orgIdentifier: string,
  code: string,
  appointmentId: string,
  supplement: PublicAppointmentContactSupplement,
): Promise<{ recorded: true }> => {
  const response = await fetch(
    portalUrl(
      orgIdentifier,
      code,
      `/appointments/${encodeURIComponent(appointmentId)}/contact-details`,
    ),
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(supplement),
    },
  )

  return parseResponse<{ recorded: true }>(response)
}

export const setContactMethodChannelPermission = async (
  orgIdentifier: string,
  code: string,
  contactPointId: string,
  channel: PublicContactMethodChannel,
  isEnabled: boolean,
): Promise<PublicContactMethodResponse> => {
  const response = await fetch(
    portalUrl(
      orgIdentifier,
      code,
      `/contact-methods/${encodeURIComponent(contactPointId)}/channel-permission`,
    ),
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        channel,
        is_enabled: isEnabled,
      } satisfies SetContactMethodChannelPermissionRequest),
    },
  )

  return parseResponse<PublicContactMethodResponse>(response)
}

export const disableContactMethodChannel = async (
  orgIdentifier: string,
  code: string,
  contactPointId: string,
  channel: PublicContactMethodChannel,
): Promise<PublicContactMethodResponse> => {
  return setContactMethodChannelPermission(orgIdentifier, code, contactPointId, channel, false)
}

export const requestContactMethodDeletion = async (
  orgIdentifier: string,
  code: string,
  contactPointId: string,
): Promise<ContactMethodDeletionRequestResponse> => {
  const response = await fetch(
    portalUrl(
      orgIdentifier,
      code,
      `/contact-methods/${encodeURIComponent(contactPointId)}/delete-request`,
    ),
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    },
  )

  return parseResponse<ContactMethodDeletionRequestResponse>(response)
}
